import React from "react";
import { useIntl } from "react-intl";
import { useOnChangeFormHook } from "src/utils/hooks/useFormHook";
import NoFooterModal from "../NoFooterModal";
import InputForm from "../../../atoms/FormInputs/InputForm";
import { ChangePasswordForm, ErrorMessage } from "./styles";
import { changePassword } from "../../../../requests/users/requestCreators";
import { changePasswordFormData } from "../../../../requests/users/types";
import { ButtonType, FlowioInputType } from "../../../../globalTypes";
import { SubmitFormButton } from "../../../atoms/inputs/EwaButtons";
import flowioast from "../../../../utils/flowioast";

export type ChangePasswordModalProps = {
  isOpen: boolean;
  toggle: () => void;
  logOut: () => void;
  userEmail: string;
};

const ChangePasswordModal = ({
  isOpen,
  toggle,
  logOut,
  userEmail,
}: ChangePasswordModalProps) => {
  const { formatMessage } = useIntl();
  const { register, handleSubmit, watch, errors } =
    useOnChangeFormHook<changePasswordFormData>();
  const newPassword = watch("newPassword");

  const onSubmit = (form: changePasswordFormData) => {
    if (!Object.keys(errors).length) {
      changePassword(form)
        .then(() => {
          logOut();
          toggle();
          flowioast.success.fromIntlBase("password.change.singular");
        })
        .catch((e) =>
          flowioast.error.fromIntlBase("password.change.singular", e)
        );
    }
  };

  return (
    <NoFooterModal
      isOpen={isOpen}
      toggle={toggle}
      title={formatMessage({ id: "login.change_password" })}
    >
      <ChangePasswordForm onSubmit={handleSubmit(onSubmit)}>
        <InputForm
          type={FlowioInputType.Password}
          name="oldPassword"
          defaultValue=""
          label={formatMessage({ id: "registration.old_password" })}
          inputRef={register({ required: true })}
        />
        {errors.oldPassword?.type === "required" && (
          <ErrorMessage>
            {formatMessage({ id: "warning.required" })}
          </ErrorMessage>
        )}
        <InputForm
          type={FlowioInputType.Password}
          name="newPassword"
          defaultValue=""
          label={formatMessage({ id: "registration.new_password" })}
          inputRef={register({
            required: true,
            minLength: 6,
          })}
        />
        {errors.newPassword?.type === "required" && (
          <ErrorMessage>
            {formatMessage({ id: "warning.required" })}
          </ErrorMessage>
        )}
        {errors.newPassword?.type === "minLength" && (
          <ErrorMessage>
            {formatMessage({ id: "warning.short_password" })}
          </ErrorMessage>
        )}
        <InputForm
          type={FlowioInputType.Password}
          defaultValue=""
          name="newPasswordAgain"
          label={formatMessage({ id: "registration.new_password_again" })}
          inputRef={register({
            required: true,
            validate: (value) => value === newPassword,
          })}
        />
        {errors.newPasswordAgain?.type === "required" && (
          <ErrorMessage>
            {formatMessage({ id: "warning.required" })}
          </ErrorMessage>
        )}
        {errors.newPasswordAgain?.type === "validate" && (
          <ErrorMessage>
            {formatMessage({ id: "warning.passwords_dont_match" })}
          </ErrorMessage>
        )}
        <SubmitFormButton
          type={ButtonType.Submit}
          label={formatMessage({ id: "login.change_password" })}
        />
      </ChangePasswordForm>
    </NoFooterModal>
  );
};

export default ChangePasswordModal;
